/* The Cookbook API returns less meal properties than the rest of the API
 * It names some properties slightly differently, like prep_minutes vs. prep_time
 */

export const cookbookMeal = PropTypes.shape({
  id: PropTypes.string.isRequired,
  spiceLevel: PropTypes.string,
  title: PropTypes.string.isRequired,
  prep: PropTypes.string.isRequired,
  photo: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  subtitle: AirbnbPropTypes.or([PropTypes.string, AirbnbPropTypes.explicitNull()]).isRequired,
});

export const DELIVERED_BASKET_STATUS = 'DELIVERED';
export const PROCESSED_BASKET_STATUS = 'PROCESSED';
export const SHIPPED_BASKET_STATUS = 'SHIPPED';
export const BELOW_MINIMUM_BASKET_STATUS = 'BELOW MINIMUM';
export const SKIPPED_BASKET_STATUS = 'SKIPPED';
export const SCHEDULED_BASKET_STATUS = 'SCHEDULED';
export const ACTIVE_BASKET_STATUS = 'ACTIVE';
export const UNDELIVERABLE_BASKET_STATUS = 'UNDELIVERABLE';
export const DONATED_BASKET_STATUS = 'DONATED';
export const MEALS_PICKED_FOR_YOU_BASKET_STATUS = 'MEALS PICKED FOR YOU';
export const CHOICES_SAVED_BASKET_STATUS = 'CHOICES SAVED';

export const basketStatus = PropTypes.oneOf([
  DELIVERED_BASKET_STATUS,
  DONATED_BASKET_STATUS,
  PROCESSED_BASKET_STATUS,
  SHIPPED_BASKET_STATUS,
  BELOW_MINIMUM_BASKET_STATUS,
  SKIPPED_BASKET_STATUS,
  SCHEDULED_BASKET_STATUS,
  ACTIVE_BASKET_STATUS,
  UNDELIVERABLE_BASKET_STATUS,
  MEALS_PICKED_FOR_YOU_BASKET_STATUS,
  CHOICES_SAVED_BASKET_STATUS,
]);

export const mealDisplayUnit = PropTypes.shape({
  increment: PropTypes.number.isRequired,
  singular: PropTypes.string.isRequired,
  plural: PropTypes.string.isRequired,
  isStandard: PropTypes.bool.isRequired,
  maxOrderableQuantity: PropTypes.number.isRequired,
});

const primaryLabelData = PropTypes.shape({
  labelText: PropTypes.string.isRequired,
  descriptionText: PropTypes.string,
  textColorHexCode: PropTypes.string,
});

export const meal = PropTypes.shape({
  id: PropTypes.string.isRequired,
  spiceLevel: PropTypes.string,
  title: PropTypes.string.isRequired,
  prepMinutes: PropTypes.string,
  photo: PropTypes.string.isRequired,
  url: PropTypes.string,
  subtitle: AirbnbPropTypes.or([PropTypes.string, AirbnbPropTypes.explicitNull()]),
  tags: PropTypes.arrayOf(PropTypes.string),
  servings: PropTypes.number,
  primaryLabel: PropTypes.string,
  secondaryLabel: PropTypes.string,
  mealAddonId: PropTypes.string,
  mealAddonName: PropTypes.string,
  displayUnit: mealDisplayUnit,
  isDonation: PropTypes.bool,
  primaryLabelData,
});

export const confirmationModalMeal = PropTypes.shape({
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  photo: PropTypes.string.isRequired,
});

export const lineItemMeal = PropTypes.shape({
  id: PropTypes.string.isRequired,
  spiceLevel: PropTypes.string,
  title: PropTypes.string.isRequired,
  prepMinutes: PropTypes.string,
  photo: PropTypes.string.isRequired,
  subtitle: AirbnbPropTypes.or([PropTypes.string, AirbnbPropTypes.explicitNull()]).isRequired,
  tags: PropTypes.arrayOf(PropTypes.string),
  servings: PropTypes.number,
  primaryLabel: PropTypes.string,
  secondaryLabel: PropTypes.string,
  displayUnit: mealDisplayUnit.isRequired,
  primaryLabelData,
});

const addressProperties = {
  streetAddress: PropTypes.string.isRequired,
  streetAddress2: PropTypes.string,
  city: PropTypes.string.isRequired,
  state: PropTypes.string.isRequired,
  zip: PropTypes.string.isRequired,
  id: PropTypes.string,
};

export const address = PropTypes.shape(addressProperties);

export const basketAddress = PropTypes.shape({
  ...addressProperties,
  default: PropTypes.bool.isRequired,
});

export const basket = PropTypes.shape({
  address: address.isRequired,
  belowMinimum: PropTypes.bool.isRequired,
  deliveryDay: PropTypes.string.isRequired,
  displayStatus: basketStatus,
  donated: PropTypes.bool.isRequired,
  futureBasket: PropTypes.bool.isRequired,
  hasActiveTracking: PropTypes.bool,
  id: PropTypes.string.isRequired,
  meals: PropTypes.arrayOf(PropTypes.object),
  pastBasket: PropTypes.bool.isRequired,
  scheduled: PropTypes.bool.isRequired,
  skippable: PropTypes.bool.isRequired,
  status: PropTypes.string.isRequired,
});

export const barebonesBasket = PropTypes.shape({
  hasActiveTracking: PropTypes.bool,
  id: PropTypes.string.isRequired,
  menu: PropTypes.shape({
    slug: PropTypes.string.isRequired,
  }).isRequired,
});

export const defaultEmptyBasket = PropTypes.shape({
  meals: PropTypes.array.isRequired,
});

export const mealOption = PropTypes.shape({
  id: PropTypes.string.isRequired,
  displayName: PropTypes.string.isRequired,
  isDefault: PropTypes.bool,
  mealOptionGroupId: PropTypes.string.isRequired,
  priceModifierCents: PropTypes.number.isRequired,
});

export const mealOptionGroup = PropTypes.shape({
  id: PropTypes.string,
  title: PropTypes.string,
  mealId: PropTypes.string,
  mealOptions: PropTypes.arrayOf(mealOption),
});

export const mealOptionSelection = PropTypes.shape({
  mealOptionGroupId: PropTypes.string,
  mealOptionId: PropTypes.string,
  displayName: PropTypes.string,
});

export const mealAddon = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  displayName: PropTypes.string.isRequired,
});

export const optIn = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
});

export const optIns = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  displayName: PropTypes.string.isRequired,
});

export const optInSelection = PropTypes.shape({
  id: PropTypes.string.isRequired,
});

export const userModel = PropTypes.shape({
  address,
  mealAddonSelections: PropTypes.arrayOf(mealAddon),
  name: PropTypes.string.isRequired,
});

export const userLoadingModel = PropTypes.shape({
  isLoaded: PropTypes.bool.isRequired,
  userLoading: PropTypes.bool,
});

export const calendarWeek = PropTypes.shape({
  deliveryDay: PropTypes.string,
  status: PropTypes.string.isRequired,
  days: PropTypes.arrayOf(PropTypes.object).isRequired, // array of MomentJS moments
  weekOf: PropTypes.string.isRequired, // date string representing Monday of week, unique key
  basketSlug: PropTypes.string.isRequired, // slug for the corresponding week's basket
});

export const calendarMonth = PropTypes.shape({
  monthName: PropTypes.string.isRequired,
  weeks: PropTypes.arrayOf(calendarWeek),
});

export const calendar = PropTypes.arrayOf(calendarMonth);

export const UNPAUSE_ACTION = 'unpause';
export const RESEND_EMAIL_CONFIRMATION_ACTION = 'resend_email_confirmation';
export const SHOW_MODAL_ACTION = 'show_modal';
export const SHOW_JANUARY_RESTART_MODAL_ACTION = 'show_january_restart_modal';

export const ICON_PAUSE = 'pause';
export const ICON_CREDIT_CARD = 'credit_card';
export const ICON_NEW = 'new_badge';

export const message = PropTypes.shape({
  action: PropTypes.string,
  actionText: PropTypes.string,
  description: PropTypes.string,
  displayRule: PropTypes.string,
  heading: PropTypes.string.isRequired,
  photo: PropTypes.string,
  placement: PropTypes.string.isRequired,
  priority: PropTypes.number.isRequired,
  url: PropTypes.string,
});

export const review = PropTypes.shape({
  id: PropTypes.string,
  recipeRating: PropTypes.number,
  comment: PropTypes.string,
  mealSelectionId: PropTypes.string,
});

export const reviewComment = PropTypes.shape({
  showCommentBox: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
  isSubmitted: PropTypes.bool.isRequired,
  submitPending: PropTypes.bool.isRequired,
  mealSelectionId: AirbnbPropTypes.or([PropTypes.string, AirbnbPropTypes.explicitNull()]),
  mealId: AirbnbPropTypes.or([PropTypes.string, AirbnbPropTypes.explicitNull()]),
});

export const mealNote = PropTypes.shape({
  id: PropTypes.string.isRequired,
  mealId: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
});

/**
 * Formik doesn't expose its injected props prop-types; declaring them here
 * manually so we can re-use them easily.
 *
 * From https://jaredpalmer.com/formik/docs/api/formik#formik-render-methods-and-props
 */
export const formikForm = {
  dirty: PropTypes.bool.isRequired,
  errors: PropTypes.object.isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleReset: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  isValid: PropTypes.bool.isRequired,
  isValidating: PropTypes.bool.isRequired,
  resetForm: PropTypes.func.isRequired,
  setErrors: PropTypes.func.isRequired,
  setFieldError: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
  submitForm: PropTypes.func.isRequired,
  submitCount: PropTypes.number.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setStatus: PropTypes.func.isRequired,
  setSubmitting: PropTypes.func.isRequired,
  setTouched: PropTypes.func.isRequired,
  setValues: PropTypes.func.isRequired,
  status: PropTypes.any,
  touched: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
  validateForm: PropTypes.func.isRequired,
  validateField: PropTypes.func.isRequired,
};

export const refType = PropTypes.oneOfType([
  PropTypes.func,
  PropTypes.shape({ current: PropTypes.PropTypes.any }),
]);

export const shoppingListIngredient = PropTypes.shape({
  name: PropTypes.string.isRequired,
  measure: PropTypes.string.isRequired,
  pantryIngredient: PropTypes.bool.isRequired,
  totalQuantity: PropTypes.number.isRequired,
  displayQuantity: PropTypes.string.isRequired,
  selected: PropTypes.bool.isRequired,
  meals: PropTypes.array.isRequired,
});

export const shoppingListMeal = PropTypes.shape({
  id: PropTypes.string.isRequired,
  slug: PropTypes.string,
  subtitle: PropTypes.string,
  title: PropTypes.string,
  url: PropTypes.string,
  ingredients: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      quantity: PropTypes.number.isRequired,
      measure: PropTypes.string.isRequired,
    })
  ),
});

export const shoppingListServings = PropTypes.objectOf(PropTypes.number);

export const menuCategory = PropTypes.shape({
  slug: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
});

export const searchParamsTypes = PropTypes.oneOfType([
  PropTypes.bool,
  PropTypes.string,
  PropTypes.number,
]);

export const searchParams = PropTypes.objectOf(searchParamsTypes);

export const minimalPastOrder = PropTypes.shape({
  id: PropTypes.string.isRequired,
  total: PropTypes.string.isRequired,
  deliveryDate: PropTypes.string.isRequired,
  chargedDate: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
});

export const pastReceipt = PropTypes.shape({
  deliveryDate: PropTypes.string.isRequired,
  donated: PropTypes.bool.isRequired,
  hasActiveTracking: PropTypes.bool,
  voucherCodeUsed: PropTypes.string,
  trackingNumber: PropTypes.string,
  trackingLink: PropTypes.string,
  refundAmount: PropTypes.string,
  subtotal: PropTypes.string.isRequired,
  shipping: PropTypes.string,
  credit: PropTypes.string,
  salesTax: PropTypes.string,
  total: PropTypes.string.isRequired,
  menu: PropTypes.shape({
    id: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
  }).isRequired,
  address: address.isRequired,
  meals: PropTypes.arrayOf(meal).isRequired,
});

export const nutritionSummary = PropTypes.shape({
  averageCalories: PropTypes.number.isRequired,
  averageProtein: PropTypes.number.isRequired,
  averageNetCarbs: PropTypes.number.isRequired,
});

export const mealBadge = PropTypes.shape({
  url: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
});

export const rectangularMealBadge = PropTypes.shape({
  url: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
});

export const pauseReasonsMeal = PropTypes.shape({
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  photo: PropTypes.string.isRequired,
  subtitle: AirbnbPropTypes.or([PropTypes.string, AirbnbPropTypes.explicitNull()]).isRequired,
  primaryLabelData,
});

export const behavioralDiscount = PropTypes.shape({
  promotionText: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  discountTriggered: PropTypes.bool.isRequired,
});

export const sendABoxMenu = PropTypes.shape({
  id: PropTypes.string,
  closed: PropTypes.bool,
  endsAt: PropTypes.string,
  slug: PropTypes.string,
  url: PropTypes.string,
  weekOfDate: PropTypes.string,
});

export const quickFilters = PropTypes.shape({
  displayName: PropTypes.string.isRequired,
  iconUrl: PropTypes.string.isRequired,
  toMealFilter: PropTypes.shape({
    filter: PropTypes.string.isRequired,
    value: PropTypes.object.isRequired,
    isQuickFilter: PropTypes.bool,
  }).isRequired,
});

const nsoAddressProperties = {
  streetAddress: PropTypes.string,
  address2: PropTypes.string,
  city: PropTypes.string,
  state: PropTypes.string,
  zipCode: PropTypes.string,
  id: PropTypes.string,
};

export const nsoAddress = PropTypes.shape(nsoAddressProperties);

export const nsoRecipientInfo = PropTypes.shape({
  id: PropTypes.string,
  address: nsoAddress,
  email: PropTypes.string,
  name: PropTypes.string,
  phone: PropTypes.string,
});

export const deliveryDates = PropTypes.shape({
  date: PropTypes.string.isRequired,
  dayOfWeek: PropTypes.string.isRequired,
});

export const box = PropTypes.shape({
  id: PropTypes.string,
  dateCreated: PropTypes.string,
  deliveryDate: PropTypes.string,
  recipientId: PropTypes.string,
  recipientEmail: PropTypes.string,
});

export const activeBox = PropTypes.shape({
  additionalBudget: PropTypes.number,
  meals: PropTypes.arrayOf(meal),
  mealPlan: PropTypes.string,
  menuClosed: PropTypes.bool,
  menuEndsAt: PropTypes.string,
  menuSlug: PropTypes.string,
  totalOrderCost: PropTypes.number,
});

export const reviewableMeal = PropTypes.shape({
  mealId: PropTypes.string.isRequired,
  menuId: PropTypes.string.isRequired,
  mealTitle: PropTypes.string.isRequired,
  mealPhoto: PropTypes.string.isRequired,
  spiceLevel: PropTypes.string.isRequired,
});

export const customerAdvocacySection = PropTypes.shape({
  key: PropTypes.string.isRequired,
  subheading: PropTypes.string.isRequired,
  route: PropTypes.string.isRequired,
  imageUrl: PropTypes.string.isRequired,
  mobileImageUrl: PropTypes.string.isRequired,
});

export const ingredient = PropTypes.shape({
  name: PropTypes.string.isRequired,
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  allergens: PropTypes.arrayOf(PropTypes.string).isRequired,
  photo: PropTypes.string,
});

export const mealReviewStats = PropTypes.shape({
  stats: PropTypes.shape({
    averageRating: PropTypes.number,
    reviewCount: PropTypes.number,
  }),
  isError: PropTypes.bool,
  isLoading: PropTypes.bool,
  isFetched: PropTypes.bool,
});
